import React from 'react';
import { CardList } from '@/components/ui/CardList';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { TablePagination } from '@/components/ui/TablePagination';
import { Eye, Pencil, Trash2, Image as ImageIcon, MapPin } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { ProjectDashboardItem } from '../types/projects1.types';

interface ProjectListBlockProps {
  projects: ProjectDashboardItem[];
  isLoading: boolean;
  total: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function ProjectListBlock({
  projects,
  isLoading,
  total,
  pageSize,
  currentPage,
  onPageChange,
  onView,
  onEdit,
  onDelete,
}: ProjectListBlockProps) {
  return (
    <div className="rounded-md border border-slate-100 bg-white  overflow-hidden flex flex-col">
      {/* Header section with title */}
      <div className="p-3 border-b border-slate-50">
        <h2 className="text-xs lg:text-sm font-normal text-slate-900 tracking-wider">Danh sách dự án</h2>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:flex flex-col flex-1 min-h-[500px] max-h-[700px]">
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(241,245,249,1)] font-normal">
              <tr className="text-xs font-normal text-slate-400 tracking-wider">
                <th className="py-4 pl-6 min-w-[180px] max-w-[220px] font-normal">Dự án</th>
                <th className="py-4 min-w-[100px] font-normal">Loại</th>
                <th className="py-4 min-w-[220px] font-normal">Chủ đầu tư & Địa điểm</th>
                <th className="py-4 min-w-[120px] font-normal">Tiến độ</th>
                <th className="py-4 min-w-[100px] font-normal">Trạng thái</th>
                <th className="py-4 pr-6 text-right w-10 font-normal"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 text-xs lg:text-sm">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={6} className="p-3">
                      <div className="h-14 rounded-md bg-slate-50" />
                    </td>
                  </tr>
                ))
              ) : projects.length > 0 ? (
                projects.map((project) => (
                  <tr key={project.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="py-3 pl-6">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-12 shrink-0 overflow-hidden rounded bg-slate-100 border border-slate-50">
                          {project.thumbnail_url ? (
                            <img src={project.thumbnail_url} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-slate-300">
                              <ImageIcon className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col min-w-0 max-w-[180px] lg:max-w-[220px]">
                          <span className="text-xs lg:text-sm font-normal text-slate-900 group-hover:text-emerald-600 transition-colors leading-tight break-words">
                            {project.name}
                          </span>
                          <span className="text-xs font-normal text-slate-400 mt-0.5">{project.code}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <TypeBadge type={project.project_type || 'other'} />
                    </td>
                    <td className="py-3">
                      <div className="flex flex-col gap-1 min-w-0 max-w-[250px]">
                        <span className="font-normal text-slate-800 leading-tight">
                          {project.client_name || 'Khách hàng lẻ'}
                        </span>
                        <div className="flex items-start gap-1 text-xs lg:text-sm font-normal text-slate-400">
                          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span className="leading-tight">{project.location || '---'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex w-28 lg:w-32 flex-col space-y-1.5">
                        <span className="text-xs lg:text-sm font-normal text-slate-900">{project.completion_pct || 0}%</span>
                        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all duration-700",
                              (project.completion_pct || 0) > 70 ? 'bg-emerald-600' : (project.completion_pct || 0) > 30 ? 'bg-amber-500' : 'bg-rose-500'
                            )}
                            style={{ width: `${project.completion_pct || 0}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <StatusBadge status={project.status_code || 'planning'} />
                    </td>
                    <td className="py-3 pr-6 text-right">
                      <ActionMenu
                        options={[
                          { label: 'Chi tiết', icon: Eye, onClick: () => onView(project.id) },
                          { label: 'Chỉnh sửa', icon: Pencil, onClick: () => onEdit(project.id) },
                          {
                            label: 'Xóa dự án',
                            icon: Trash2,
                            destructive: true,
                            onClick: () => onDelete?.(project.id)
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-20 text-center text-slate-400">
                    <p className="text-xs lg:text-sm font-normal italic">Không tìm thấy dự án nào khớp với bộ lọc.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden p-3 space-y-4 bg-slate-50/30">
        <CardList
          items={projects}
          isLoading={isLoading}
          renderCard={(project) => (
            <div key={project.id} className="p-3 space-y-4 border border-slate-100 rounded-md bg-white  active:scale-[0.98] transition-all" onClick={() => onView(project.id)}>
              <div className="flex items-start gap-3">
                <div className="h-14 w-16 shrink-0 overflow-hidden rounded bg-slate-50">
                  {project.thumbnail_url ? (
                    <img src={project.thumbnail_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-200">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <TypeBadge type={project.project_type || 'other'} />
                    <StatusBadge status={project.status_code || 'planning'} />
                  </div>
                  <h4 className="text-xs lg:text-sm font-normal text-slate-900 truncate leading-tight">{project.name}</h4>
                  <p className="text-xs lg:text-sm font-normal text-slate-400 mt-0.5 mb-2">{project.code}</p>

                  <div className="space-y-1 pt-1 border-t border-slate-50">
                    <p className="text-xs font-normal text-slate-400 tracking-wider">Chủ đầu tư & Địa điểm</p>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs lg:text-sm font-normal text-slate-700">{project.client_name || 'Khách hàng lẻ'}</span>
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{project.location || '---'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-50 flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-xs lg:text-sm font-normal text-slate-400">Tiến độ</p>
                  <p className="text-xs lg:text-sm font-normal text-emerald-600">{project.completion_pct || 0}%</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={(e) => { e.stopPropagation(); onEdit(project.id); }} className="h-8 w-8 rounded-md bg-slate-50 text-slate-400 flex items-center justify-center hover:text-emerald-600 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete?.(project.id); }}
                    className="h-8 w-8 rounded-md bg-rose-50 text-rose-500 flex items-center justify-center hover:bg-rose-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        />
      </div>

      {/* Pagination Footer */}
      <div className="p-3 border-t border-slate-50 flex items-center justify-center lg:justify-between">
        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={total}
          onPageChange={onPageChange}
          itemLabel="dự án"
        />
      </div>
    </div>
  );
}

function TypeBadge({ type }: { type: string }) {
  const configs: Record<string, { label: string; class: string }> = {
    construction: { label: 'Thi công', class: 'bg-blue-50 text-blue-600' },
    maintenance: { label: 'Bảo dưỡng', class: 'bg-emerald-50 text-emerald-600' },
    landscape: { label: 'Cảnh quan', class: 'bg-teal-50 text-teal-600' },
    other: { label: 'Khác', class: 'bg-slate-50 text-slate-600' },
  };

  const config = configs[type] || configs.other;
  return <span className={cn('rounded-md px-2 py-0.5 text-xs lg:text-sm font-normal', config.class)}>{config.label}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; class: string }> = {
    planning: { label: 'Chuẩn bị', class: 'bg-orange-50 text-orange-600' },
    active: { label: 'Đang thực hiện', class: 'bg-emerald-50 text-emerald-600' },
    construction: { label: 'Đang thực hiện', class: 'bg-blue-50 text-blue-600' },
    completed: { label: 'Hoàn thành', class: 'bg-blue-50 text-blue-600' },
    on_hold: { label: 'Tạm dừng', class: 'bg-orange-50 text-orange-600' },
  };

  const config = configs[status] || configs.planning;
  return <span className={cn('rounded-md px-2 py-0.5 text-xs lg:text-sm font-normal', config.class)}>{config.label}</span>;
}
